'use client';

import { Box, Button, Container, Typography } from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error, {
      tags: {
        component: 'global-error',
        crash: 'true',
        environment: process.env.NEXT_PUBLIC_ENV,
      },
      extra: {
        digest: error?.digest,
        message: error?.message,
        stack: error?.stack,
        name: error?.name,
        pathname: window?.location?.pathname,
        timestamp: new Date().toISOString(),
      },
      level: 'error',
    });
  }, [error]);

  return (
    <html lang="en">
      <body>
        <Container
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            textAlign: 'center',
            py: 4,
          }}
        >
          <Box mb={4}>
            <Typography variant="h4" component="h1" gutterBottom>
              Something went wrong!
            </Typography>

            <Typography variant="body1" color="text.secondary" mb={4}>
              We&apos;ve been notified about this issue and we&apos;re working
              to fix it.
            </Typography>

            <Button
              variant="contained"
              color="primary"
              onClick={() => reset()}
              sx={{ mt: 2 }}
            >
              Try again
            </Button>
          </Box>

          {process.env.NODE_ENV === 'development' && (
            <Box
              sx={{
                mt: 4,
                p: 2,
                backgroundColor: 'rgba(0,0,0,0.05)',
                borderRadius: 1,
                maxWidth: '100%',
                overflow: 'auto',
              }}
            >
              <Typography
                variant="body2"
                component="pre"
                sx={{ whiteSpace: 'pre-wrap' }}
              >
                {error.message}
                {'\n'}
                {error.stack}
              </Typography>
            </Box>
          )}
        </Container>
      </body>
    </html>
  );
}
